import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Checkbox,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../api';
import SettingsPageLayout from './SettingsPageLayout';

const SCOPES = [
  { value: 'clients:read', label: 'Clients — Read' },
  { value: 'clients:write', label: 'Clients — Write' },
  { value: 'invoices:read', label: 'Invoices — Read' },
  { value: 'invoices:write', label: 'Invoices — Write' },
  { value: 'tasks:read', label: 'Tasks — Read' },
  { value: 'tasks:write', label: 'Tasks — Write' },
  { value: 'workflows:read', label: 'Workflows — Read' },
  { value: 'workflows:write', label: 'Workflows — Write' },
  { value: 'documents:read', label: 'Documents — Read' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function CreatedKeyDialog({ open, apiKey, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = apiKey?.key || '';
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // copy not supported — user must copy manually
    } finally {
      document.body.removeChild(el);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>API Key Created</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Copy this key now. It will not be shown again.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            fontFamily: 'monospace',
            fontSize: 13,
            wordBreak: 'break-all',
          }}
        >
          <Box sx={{ flex: 1 }}>{apiKey?.key}</Box>
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          I've saved my key
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GenerateKeyDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setName('');
    setSelectedScopes([]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleScope = (val) => {
    setSelectedScopes((prev) =>
      prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val]
    );
  };

  const handleGenerate = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('Select at least one permission.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/api/developer/api-keys/', {
        name: name.trim(),
        permissions: selectedScopes,
      });
      resetForm();
      onCreated(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to create API key.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate API Key</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Key Name"
            placeholder="e.g. Zapier Integration"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Permissions
            </Typography>
            <Stack spacing={0.5}>
              {SCOPES.map((scope) => (
                <FormControlLabel
                  key={scope.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedScopes.includes(scope.value)}
                      onChange={() => toggleScope(scope.value)}
                    />
                  }
                  label={<Typography variant="body2">{scope.label}</Typography>}
                  sx={{ ml: 0 }}
                />
              ))}
            </Stack>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Generating…' : 'Generate Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RevokeDialog({ open, keyName, onClose, onConfirm, revoking }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Revoke API Key</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Revoke <strong>{keyName}</strong>? Any integrations using this key will stop working immediately.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={revoking}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={revoking}
          startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {revoking ? 'Revoking…' : 'Revoke'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const DeveloperSettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false });

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get('/api/developer/api-keys/');
      setKeys(res.data.api_keys ?? res.data);
    } catch {
      setLoadError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreated = (data) => {
    setGenerateOpen(false);
    setCreatedKey(data);
    loadKeys();
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/api/developer/api-keys/${revokeTarget.id}/`);
      setRevokeTarget(null);
      setSnackbar({ open: true, message: 'API key revoked.', severity: 'success' });
      loadKeys();
    } catch {
      setSnackbar({ open: true, message: 'Failed to revoke key.', severity: 'error' });
    } finally {
      setRevoking(false);
    }
  };

  return (
    <SettingsPageLayout
      title="Developer"
      description="Manage API keys for programmatic access to your firm's data. Keys authenticate requests via the X-API-Key header."
      maxWidth={800}
      noCard
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          API Keys
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setGenerateOpen(true)}
        >
          Generate Key
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : loadError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {loadError}
          </Alert>
        ) : keys.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <VisibilityOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No API keys yet. Generate one to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Permissions</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Last Used</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {k.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}
                      >
                        ••••••••{k.prefix}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(k.permissions || []).map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(k.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(k.last_used_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke key">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRevokeTarget(k)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pass your API key in the request header:
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            fontFamily: 'monospace',
            fontSize: 12,
            color: 'text.primary',
          }}
        >
          X-API-Key: your_key_here
        </Box>
      </Box>

      <GenerateKeyDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onCreated={handleCreated}
      />

      <CreatedKeyDialog
        open={Boolean(createdKey)}
        apiKey={createdKey}
        onClose={() => setCreatedKey(null)}
      />

      <RevokeDialog
        open={Boolean(revokeTarget)}
        keyName={revokeTarget?.name}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        revoking={revoking}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity || 'success'} onClose={() => setSnackbar({ open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SettingsPageLayout>
  );
};

export default DeveloperSettings;
